/// FOURJS_START_COPYRIGHT(D,2015)
/// Property of Four Js*
/// (c) Copyright Four Js 2015, 2026. All Rights Reserved.
/// * Trademark of Four Js Development Tools Europe Ltd
///   in the United States and elsewhere
///
/// This file can be modified by licensees according to the
/// product manual.
/// FOURJS_END_COPYRIGHT

"use strict";

modulum("TextEditWidget", ["FieldWidgetBase", "WidgetFactory"], function (context, cls) {
  /**
   * TextEdit widget.
   * @class TextEditWidget
   * @memberOf classes
   * @extends classes.FieldWidgetBase
   * @publicdoc Widgets
   */

  cls.TextEditWidget = context.oo.Class(cls.FieldWidgetBase, function ($super) {
    return /** @lends classes.TextEditWidget.prototype */ {
      __name: "TextEditWidget",
      /**
       * true if textedit can contains HTML content
       * @type {boolean}
       */
      _hasHTMLContent: false,
      /**
       * true if we must sanitize the html
       * @type {boolean}
       * */
      _sanitize: null,
      /** @type classes.HtmlFilterWidget **/
      _htmlFilter: null,
      /** @type {boolean} */
      _wantReturns: true,
      /** @type {boolean} */
      _wantTabs: false,
      /** @type {string} */
      _oldInnerHTML: null,
      /**
       * Store all managed key events
       * @type {Object}
       */
      _managedKeyEvents: null,
      /** For cache value */
      $static: {
        _sanitisationCache: {},
        _keys: [],
        getCacheValue: function (key) {
          if (!this._sanitisationCache[key]) {
            return null;
          }
          // Update the order
          this._keys.splice(this._keys.indexOf(key), 1);
          this._keys.unshift(key);

          return this._sanitisationCache[key];
        },
        setCacheValue: function (key, value) {
          if (!this._sanitisationCache[key]) {
            // Delete the less recently used value
            if (this._keys.length >= gbc.ThemeService.getValue("gbc-TextEditWidget-capacity")) {
              let oldestKey = this._keys.pop();
              delete this._sanitisationCache[oldestKey];
            }
            this._keys.unshift(key);
          } else {
            // if the key already exist update the order
            this._keys.splice(this._keys.indexOf(key), 1);
            this._keys.unshift(key);
          }
          this._sanitisationCache[key] = value;
        }
      },
      /**
       * Redefine where the data is located
       * @type {string|Object}
       */
      __dataContentPlaceholderSelector: cls.WidgetBase.selfDataContent,

      constructor: function (opts) {
        $super.constructor.call(this, opts);
      },

      /**
       * @inheritDoc
       */
      _initLayout: function () {
        if (!this._ignoreLayout) {
          this._layoutInformation = new cls.LayoutInformation(this);
          this._layoutInformation.shouldFillStack = true;
          this._layoutEngine = new cls.TextEditLayoutEngine(this);
          this._layoutEngine._shouldFillHeight = true;
          // shouldn't support sizepolicy dynamic, so we override it
          this._layoutInformation.getSizePolicyConfig().dynamic = cls.SizePolicy.Initial();
          this._layoutInformation.getSizePolicyConfig().setAllShrinkable(true);
          this._layoutInformation.forcedMinimalWidth = 20;
          this._layoutInformation.forcedMinimalHeight = 20;
        }
      },

      /**
       * @inheritDoc
       */
      _initElement: function () {
        $super._initElement.call(this);
        this._inputElement = this.getElement().getElementsByTagName("textarea")[0];
        this._initListeners();
      },

      /**
       * Initialize all widget events listener
       */
      _initListeners: function () {
        if (cls.WindowHelper.isMobile()) {
          // Track the focus and mouse down/up events on mobile devices to handle the virtual keyboard's TAB key
          cls.DOMElementHelper.on(this._inputElement, "focus.FieldWidgetBase", this._onMobileFocus.bind(this));
        }
      },

      /**
       * Clear event listeners of the widget
       */
      _unloadListeners: function () {
        if (cls.WindowHelper.isMobile()) {
          cls.DOMElementHelper.off(this._inputElement, "focus.FieldWidgetBase");
        }
      },

      /**
       * @inheritDoc
       */
      destroy: function () {
        this._unloadListeners();

        if (this._htmlFilter) {
          this._htmlFilter.destroy();
          this._htmlFilter = null;
        }
        this._managedKeyEvents = null;

        $super.destroy.call(this);
      },

      /**
       * @inheritDoc
       */
      manageMouseDown: function (domEvent) {
        $super.manageMouseDown.call(this, domEvent);

        // Manage requestFocus during selection of text
        cls.WidgetBase._onSelect.call(this, domEvent);
        return true;
      },

      /**
       * @inheritDoc
       */
      manageMouseClick: function (domEvent) {
        this._onRequestFocus(domEvent); // request focus
        this.emit(context.constants.widgetEvents.click, domEvent);
        return $super.manageMouseClick.call(this, domEvent);
      },

      /**
       * @inheritDoc
       */
      managePriorityKeyDown: function (keyString, domKeyEvent, repeat) {
        if (!this.isEnabled()) {
          return false;
        }
        let keyProcessed = false;

        switch (keyString) {
          case "up":
          case "down":
          case "home":
          case "end":
            domKeyEvent.allowDefault = true;
            keyProcessed = true;
            break;
          case "return":
          case "shift+return":
          case "enter":
          case "shift+enter":
            if (!this.isNotEditable() && this._wantReturns) {
              this._insertChar("\n");
              keyProcessed = true;
            }
            break;
          case "tab":
            if (!this.isNotEditable() && this._wantTabs) {
              this._insertChar("\t");
              keyProcessed = true;
            }
            break;
        }

        if (keyProcessed) {
          return true;
        }
        return $super.managePriorityKeyDown.call(this, keyString, domKeyEvent, repeat);
      },

      /**
       * @inheritDoc
       */
      _checkValue: function (text, newTextPart) {
        if (this._dialogType !== "Input" && this._dialogType !== "InputArray") {
          return newTextPart;
        }

        if (this._maxLength > 0 && !this.getUserInterfaceWidget().isCharLengthSemantics()) {
          newTextPart = this.checkValueByteCount(text, newTextPart, this._maxLength);
        }

        return newTextPart;
      },

      /**
       * @inheritDoc
       */
      manageInput: function (dataString = "", event = null) {
        $super.manageInput.call(this, dataString, event);

        if (!this._hasHTMLContent) {
          this.controlValueLength(event);
        }
        if (!this.getInputTextState().isRestored()) {
          this.triggerValueChangedEvent(this.getValue(), false);
        }
      },

      /**
       * Insert a TAB char
       * @private
       */
      _insertChar: function (char) {
        const selectionStart = this._inputElement.selectionStart;
        const inputValue = this.getInputElement().value;
        const value = inputValue.substring(0, this._inputElement.selectionStart) + char + inputValue.substring(this._inputElement.selectionEnd);
        // If Maxlength is not defined, insert tab character anyway
        if (!this.getMaxLength() || value.length <= this.getMaxLength()) {
          this.setValue(value);
          this.getInputElement().selectionEnd = selectionStart + 1;
        }
      },

      /**
       * @inheritDoc
       */
      setValue: function (value, fromVM = false, cursorPosition = null) {
        $super.setValue.call(this, value, fromVM, cursorPosition);
        this._setValue(value);

        if (cursorPosition && this.isEnabled()) {
          this._inputElement.selectionStart = this._inputElement.selectionEnd = cursorPosition;
        }
      },

      /**
       * Internal setValue used to be inherited correctly by DummyWidget
       * @param {string} value - the value
       * @private
       */
      _setValue: function (value) {
        if (this._hasHTMLContent) {
          if (this._sanitize) {
            if (value === "") {
              if (value !== this.oldInnerHTML) {
                this._inputElement.innerHTML = value;
                this.oldInnerHTML = value;
              }

              // If empty string, then we just ignore
              return;
            }

            // If the value is not in the cache, then we clean it
            if (!cls.TextEditWidget.getCacheValue(value)) {
              // Clean and update the cache
              cls.TextEditWidget.setCacheValue(value, this._htmlFilter.sanitize(value));
            }

            const valueGet = cls.TextEditWidget.getCacheValue(value);
            if (valueGet !== this.oldInnerHTML) {
              this._inputElement.innerHTML = valueGet;
              this.oldInnerHTML = valueGet;
            }
          } else {
            this._inputElement.innerHTML = value;
          }
        } else if (this._inputElement.value !== value) {
          if (!this._enabled || this._isReadOnly) {
            this._inputElement.scrollTop = 0; // reset scrollbar when setting value in display mode
          }
          this._inputElement.value = value;
        }
      },

      /**
       * @inheritDoc
       */
      getValue: function () {
        if (this._hasHTMLContent === true) {
          return this._inputElement.innerHTML;
        }
        if (this._editingTime === 0) {
          // not touched by user
          // return exact VM value to avoid bad conversion from \r to \n
          return this._valueStack[this._valueStackCursor] || "";
        }
        let result = this._inputElement.value;
        if (this.isEditing()) {
          result = this.transformText(result);
        }
        return result;
      },

      /**
       * @inheritDoc
       */
      setReadOnly: function (readonly) {
        $super.setReadOnly.call(this, readonly);
        this._setInputReadOnly(readonly || this._notEditable || !this._enabled);
      },

      /**
       * Set input readonly attribute if it doesn't have focus or is noentry.
       * @param {boolean} readonly - true to set the edit part as read-only, false otherwise
       */
      _setInputReadOnly: function (readonly) {
        if (readonly || this._isReadOnly) {
          if (this._hasHTMLContent) {
            if (this._inputElement.hasAttribute("contenteditable")) {
              this._inputElement.removeAttribute("contenteditable");
            }
          } else {
            this._inputElement.setAttribute("readonly", "readonly");
          }
        } else if (this._hasHTMLContent) {
          if (!this._inputElement.hasAttribute("contenteditable")) {
            this._inputElement.setAttribute("contenteditable", true);
          }
        } else {
          this._inputElement.removeAttribute("readonly");
        }
      },

      /**
       * @inheritDoc
       */
      setMaxLength: function (maxLength) {
        $super.setMaxLength.call(this, maxLength);
        if (maxLength > 0) {
          this._setElementAttribute("maxlength", maxLength, "_inputElement");
        }
      },

      /**
       * @inheritDoc
       */
      setTextAlign: function (align) {
        $super.setTextAlign.call(this, align);
        this.setStyle(">textarea", {
          "text-align": align
        });
      },

      /**
       * Replace default textarea element used to display text with a html element which can displays HTML
       * @param {HTMLElement} jcontrol - div element
       * @publicdoc
       */
      setHtmlControl: function (jcontrol) {
        if (this._htmlFilter === null) {
          this._htmlFilter = cls.WidgetFactory.createWidget("HtmlFilter", this.getBuildParameters());
        }
        if (this.isEnabled()) {
          jcontrol.setAttribute("contenteditable", true);
        }
        jcontrol.innerHTML = this.getValue();
        // Remove events before replacing the widget
        this._unloadListeners();
        // Replace the content of the widget
        this.getElement().replaceChild(jcontrol, this._inputElement);
        this._inputElement = jcontrol;
        this._hasHTMLContent = true;
        // Initialize events for the new widget
        this._initListeners();
        if (this.hasFocus()) {
          // Force focus again if it has focus
          this.setFocus();
        }
      },

      /**
       * @inheritDoc
       */
      handleClipboardEvent: function () {
        // we cannot manage clipboard events when text has HTML content
        return !this._hasHTMLContent;
      },

      /**
       * @inheritDoc
       */
      shouldShowApplicationContextMenu: function () {
        // let the browser contextmenu when text has HTML content
        return !this._hasHTMLContent;
      },

      /**
       * sanitize = false : Authorize to send html text without control
       * @param {boolean} sanitize
       */
      setSanitize: function (sanitize) {
        this._sanitize = sanitize;
      },

      /**
       * @inheritDoc
       */
      setFocus: function (fromMouse = false, preventScroll = false) {
        if (cls.WindowHelper.isMobile() && this._showVirtualKeyboard === gbc.constants.showVirtualKeyboard.onTap && !fromMouse) {
          this._inputElement.setAttribute("inputmode", "none");
          this.domFocus();
          this._inputElement.removeAttribute("inputmode");
        } else {
          this.domFocus(preventScroll);
        }

        $super.setFocus.call(this, fromMouse, preventScroll);
        this._inputElement.scrollTop = 0;
      },

      /**
       * Set number of editable rows inside the textedit widget
       * @param {number} rows - number of rows
       * @publicdoc
       */
      setRows: function (rows) {
        this._inputElement.setAttribute("rows", rows || 1);
      },

      /**
       * Make the textedit content return the new line breaking the word or not
       * @param {string} format - css value
       * @publicdoc
       */
      setWrapPolicy: function (format) {
        cls.DOMElementHelper.toggleClass(this._inputElement, "breakword", format === "anywhere");
      },

      /**
       * @inheritDoc
       */
      setEnabled: function (enabled) {
        $super.setEnabled.call(this, enabled);
        this._setInputReadOnly(!enabled || this._notEditable);
      },

      /**
       * Get if textedit accepts TAB key
       * @returns {boolean} wantTabs - true if TAB key should be accepted in the textedit
       * @publicdoc
       */
      getWantTabs: function () {
        return this._wantTabs;
      },

      /**
       * Set if textedit accepts TAB key
       * @param {boolean} wantTabs - true if TAB key should be accepted in the textedit
       * @publicdoc
       */
      setWantTabs: function (wantTabs) {
        this._wantTabs = wantTabs;
      },

      /**
       * Get if textedit accepts RETURN/ENTER key
       * @returns {boolean} wantReturns - true if returns/enters should be accepted in the textedit
       * @publicdoc
       */
      getWantReturns: function () {
        return this._wantReturns;
      },

      /**
       * Set if textedit accepts RETURN/ENTER key
       * @param {boolean} wantReturns - true if returns/enters should be accepted in the textedit
       * @publicdoc
       */
      setWantReturns: function (wantReturns) {
        this._wantReturns = wantReturns;
      },

      /**
       * Defines the scrollBars to display
       * @param {string} scrollBars - value can be 'auto', 'both', 'none', 'horizontal', 'vertical'
       */
      setScrollBars: function (scrollBars) {
        this.addClasses("scrollbars-" + scrollBars);
      },

      /**
       * @inheritDoc
       */
      getValueForClipboard: function (ignoreSelection = false) {
        if (this._hasHTMLContent) {
          const txt = cls.WindowHelper.getSelectionText();
          return txt.length > 0 ? txt : null;
        }

        return $super.getValueForClipboard.call(this, ignoreSelection);
      },

      /**
       * @inheritDoc
       */
      manageClipboardPaste: function (text) {
        $super.manageClipboardPaste.call(this, text);
        this._inputElement.scrollTop = this._inputElement.scrollHeight;
      },

      /**
       * @inheritDoc
       */
      getClipboardAuthorizedAction: function () {
        return {
          paste: true,
          copy: true,
          cut: !this._hasHTMLContent, //Too much work to cut and maintain a correct HTML
          selectAll: true
        };
      }
    };
  });
  cls.WidgetFactory.registerBuilder("TextEdit", cls.TextEditWidget);
});
